import React, { useState } from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import { FormComponent, ModalComponent, UiComponent } from 'components';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import { usePageOption } from 'hooks';
import useModal from 'hooks/useModal';
import { StickerQuery } from 'queries';
import * as M from 'styles/mobile';
import {
  IStickerHolder,
  IStickerListItem,
  SearchStickerHolderFilterType,
} from 'types';
import * as S from './style';

const getPhoneLast4 = (phone?: string) =>
  (phone ?? '').replace(/\D/g, '').slice(-4);

interface Props {
  sticker: IStickerListItem;
  onClose: () => void;
}

function HoldersSheet({ sticker, onClose }: Props) {
  const [filter, setFilter] = useState<SearchStickerHolderFilterType>({});
  const { pageOption, handlePage } = usePageOption(10);
  const { handleConfirm } = useModal();
  const { mutateAsync: revoke } = StickerQuery.useRevoke();
  const { data, isLoading } = StickerQuery.useGetHolders(sticker.id, {
    page: pageOption.page,
    size: pageOption.size,
    ...filter,
  });

  const holders: IStickerHolder[] = data?.data ?? [];

  const handleSearch = (d: SearchStickerHolderFilterType) => {
    setFilter({ name: d.name || undefined });
    handlePage({ ...pageOption, page: 1 });
  };

  const handleRevoke = ({ userId, userName }: IStickerHolder) => {
    handleConfirm({
      confirmContent: `'${userName}' 님에게서 '${sticker.name}' 스티커를 회수하시겠습니까?`,
      yesTitle: '회수',
      confirmNext: () => {
        revoke({ id: sticker.id, userIds: [userId] })
          .then(() => toast.success(`${userName}님에게서 회수했습니다`))
          .catch(() => toast.error(DEFAULT_ERROR_MESSAGE));
      },
    });
  };

  return (
    <ModalComponent.Portal>
      <ModalComponent.Background>
        <ModalComponent.Modal
          title={`${sticker.name} 지급내역 (${(data?.total ?? 0).toLocaleString()}명)`}
          handleClose={onClose}
          footer={
            <UiComponent.Pagination
              pageOption={pageOption}
              handlePage={handlePage}
              totalPage={data?.totalPages || 1}
            />
          }
        >
          <div css={S.sheet}>
            <FormComponent.Form submit={handleSearch}>
              <FormComponent.SearchInput
                name="name"
                placeholder="이름으로 검색해주세요"
              />
            </FormComponent.Form>
            <div css={S.sheetList}>
              {holders.map((holder) => {
                const { id, userName, userPhone, acquiredAt, grantedBy } =
                  holder;
                const last4 = getPhoneLast4(userPhone);
                return (
                  <div key={id} css={S.row}>
                    <div css={S.info}>
                      <p css={S.name}>
                        {userName}
                        {last4 && <span>({last4})</span>}
                      </p>
                      <p css={S.meta}>
                        {dayjs(acquiredAt).format('YYYY-MM-DD HH:mm')}
                        {grantedBy && ` · ${grantedBy} 지급`}
                      </p>
                    </div>
                    <UiComponent.Button
                      variant="border"
                      color="error"
                      css={S.action}
                      onClick={() => handleRevoke(holder)}
                    >
                      회수
                    </UiComponent.Button>
                  </div>
                );
              })}
              {!isLoading && holders.length === 0 && (
                <p css={M.empty}>지급내역이 없습니다</p>
              )}
            </div>
          </div>
        </ModalComponent.Modal>
      </ModalComponent.Background>
    </ModalComponent.Portal>
  );
}

export default HoldersSheet;
